import * as THREE from 'three';
import {
  applyLeanToPose,
  createLeanTo,
  LEAN_TO_COST,
  placeLeanToInFront,
} from '../build/LeanTo';
import {
  createCampPiece,
  isPieceKind,
  PIECE_COST,
  SnapYard,
  type ChargeSource,
  type PieceKind,
} from '../build/Snap';
import { nearestInteract, queryInteract, type InteractTarget, type Xyz } from '../interact/Interact';
import type { Grid } from '../inventory/Grid';
import type { DayClock } from '../survival/DayClock';
import { VITAL_MAX, type Vitals } from '../survival/Vitals';
import { cookRaw, eatCooked } from './Cook';
import { canCraftTorch, craftTorch } from '../tools/Tools';
import { setPrompt } from '../ui/Prompt';
import { CC_NODES } from '../world/areas/a01/CrashCoast';

export const WOOD_FIRE = 2;
export const WOOD_LEANTO = LEAN_TO_COST.wood;
export const FIBER_LEANTO = LEAN_TO_COST.fiber;
export const DIRTY_THIRST = 20;
export const CLEAN_THIRST = 35;
export const SLEEP_HOLD_S = 1.5;
export const SLEEP_TOD_MIN = 5 * 60 + 30;
export const SLEEP_HUNGER = -15;
export const SLEEP_THIRST = -20;
export const OCEAN_DRINK_Z = 14;
export const EMBER_USE_RANGE = 4;

const EMBER = CC_NODES['CC-EMBER'] ?? { x: -18, y: 0.4, z: -40 };
const WATER = { x: 2, y: 0.12, z: 16.5 };

const _look = new THREE.Vector3();

export type CampHook = {
  shelter: boolean;
  fireLit: boolean;
  lastDrink: number | null;
  debug?: {
    leanToUp?: boolean;
    fireLit?: boolean;
    buildOpen?: boolean;
    ghosting?: boolean;
    pieces?: { kind: string; integrity: number; x: number; z: number }[];
  };
};

export type CampTick = {
  camera: THREE.Camera;
  player: Xyz;
  yaw: number;
  dt: number;
  use: boolean;
  build: boolean;
  fire: boolean;
  interactDown: boolean;
};

export function canPlaceLeanTo(
  inv: { countOf(id: string): number },
  placed = false,
): boolean {
  return !placed && inv.countOf('wood') >= WOOD_LEANTO && inv.countOf('fiber') >= FIBER_LEANTO;
}

export function consumeLeanTo(inv: Grid): boolean {
  if (!canPlaceLeanTo(inv)) {
    return false;
  }
  return inv.remove('wood', WOOD_LEANTO) && inv.remove('fiber', FIBER_LEANTO);
}

export class CampDirector {
  fireLit = false;
  shelter = false;
  lastDrink: number | null = null;
  ghosting = false;
  /** B toggles this. Recipe HUD is hidden until the menu is open. */
  menuOpen = false;
  selected: PieceKind = 'lean-to';
  readonly yard = new SnapYard();
  private slept = false;
  private sleepHold = 0;
  private readonly ghostHold: THREE.Group;
  private ghostMesh: THREE.Group;
  private placed: THREE.Group | null = null;
  private readonly fireLight: THREE.PointLight;
  private readonly flames: THREE.Group;
  private readonly waterMesh: THREE.Mesh;
  private readonly emberTarget: InteractTarget;
  private readonly waterTarget: InteractTarget;
  private leanTarget: InteractTarget | null = null;
  private readonly pieceTargets: InteractTarget[] = [];

  constructor(
    private readonly scene: THREE.Scene,
    private readonly inventory: Grid,
    private readonly vitals: Vitals,
    private readonly day: DayClock,
  ) {
    this.ghostHold = new THREE.Group();
    this.ghostHold.name = 'CC-BUILD-GHOST';
    this.ghostMesh = createLeanTo(true);
    this.ghostMesh.visible = false;
    this.ghostHold.add(this.ghostMesh);
    scene.add(this.ghostHold);

    this.waterMesh = new THREE.Mesh(
      new THREE.CircleGeometry(1.6, 14),
      new THREE.MeshStandardMaterial({
        color: 0x2a6570,
        transparent: true,
        opacity: 0.42,
        roughness: 0.28,
        metalness: 0.08,
        depthWrite: false,
      }),
    );
    this.waterMesh.rotation.x = -Math.PI / 2;
    this.waterMesh.position.set(WATER.x, WATER.y, WATER.z);
    this.waterMesh.name = 'CC-WATER';
    this.waterMesh.userData.id = 'CC-WATER';
    scene.add(this.waterMesh);

    const existing = scene.getObjectByName('CC-EMBER-LIGHT');
    if (existing instanceof THREE.PointLight) {
      this.fireLight = existing;
      this.fireLight.intensity = 0;
    } else {
      this.fireLight = new THREE.PointLight(0xff6a22, 0, 16, 1.8);
      this.fireLight.position.set(EMBER.x, EMBER.y + 0.95, EMBER.z);
      this.fireLight.name = 'CC-EMBER-LIGHT';
      scene.add(this.fireLight);
    }

    this.flames = makeFlames();
    this.flames.position.set(EMBER.x, EMBER.y + 0.55, EMBER.z);
    this.flames.visible = false;
    scene.add(this.flames);

    this.emberTarget = {
      id: 'CC-EMBER',
      prompt: 'E — Light fire (2 wood)',
      position: { x: EMBER.x, y: EMBER.y, z: EMBER.z },
      radius: 1.2,
      live: true,
    };
    this.waterTarget = {
      id: 'CC-WATER',
      prompt: 'E — Drink (dirty)',
      position: { ...WATER },
      radius: 5,
      live: true,
    };
  }

  tick(input: CampTick): boolean {
    if (this.ghosting || this.menuOpen) {
      applyLeanToPose(this.ghostHold, placeLeanToInFront(input.player, input.yaw));
    }

    if (input.build) {
      return this.onBuild(input.player, input.yaw);
    }
    if (input.fire) {
      if (this.nearEmber(input.player) || this.fireLit) {
        return this.useFire();
      }
      if (this.inventory.countOf('wood') >= WOOD_FIRE) {
        return this.lightFire();
      }
      setPrompt('Need 2 wood, then F at Camp Ember');
      return true;
    }

    const hit = this.query(input.camera, input.player);
    this.updateSleep(input, hit);

    if (!input.use) {
      return hit !== null || this.ghosting;
    }
    return this.use(hit, input.player, input.yaw);
  }

  /** QA: look hit, else nearest, else Ember if close / ocean if z>14. */
  forceUse(camera: THREE.Camera, player: Xyz): boolean {
    const hit = this.query(camera, player) ?? this.nearest(player);
    if (this.nearEmber(player)) {
      return this.useFire();
    }
    if (player.z > OCEAN_DRINK_Z) {
      return this.drinkDirty();
    }
    return this.use(hit, player, 0);
  }

  commitLeanTo(player: Xyz, yaw: number): boolean {
    if (!canPlaceLeanTo(this.inventory, this.shelter)) {
      return false;
    }
    if (!this.yard.canPlace()) {
      return false;
    }
    if (!consumeLeanTo(this.inventory)) {
      return false;
    }
    this.seatLeanTo(placeLeanToInFront(player, yaw));
    return true;
  }

  placePiece(kind: string, player: Xyz, yaw: number): boolean {
    if (!isPieceKind(kind)) {
      return false;
    }
    if (kind === 'lean-to') {
      return this.commitLeanTo(player, yaw);
    }
    if (!this.yard.canPlace()) {
      return false;
    }
    const cost = PIECE_COST[kind];
    if (this.inventory.countOf('wood') < cost.wood || this.inventory.countOf('fiber') < cost.fiber) {
      return false;
    }
    if (cost.wood > 0 && !this.inventory.remove('wood', cost.wood)) {
      return false;
    }
    if (cost.fiber > 0 && !this.inventory.remove('fiber', cost.fiber)) {
      return false;
    }
    const pose = placeLeanToInFront(player, yaw);
    const mesh = createCampPiece(kind);
    applyLeanToPose(mesh, pose);
    this.scene.add(mesh);
    this.yard.place(kind, pose, mesh);
    this.bindPiece(kind, pose);
    return true;
  }

  tickCharges(dinos: readonly ChargeSource[], dt: number): void {
    const broken = this.yard.tickCharges(dinos, dt);
    for (const piece of broken) {
      if (piece.mesh) {
        this.scene.remove(piece.mesh);
      }
      if (piece.kind === 'lean-to') {
        this.wreckLeanTo();
      }
    }
  }

  query(camera: THREE.Camera, player: Xyz) {
    camera.getWorldDirection(_look);
    return queryInteract(player, _look, player, this.targets());
  }

  nearest(player: Xyz) {
    return nearestInteract(player, this.targets());
  }

  syncPrompt(camera: THREE.Camera, player: Xyz): void {
    if (this.menuOpen && this.ghosting) {
      setPrompt(`B — Place ${this.selected}  ·  Q cycle  ·  Esc close`);
      return;
    }
    if (this.menuOpen) {
      setPrompt(this.needLine(this.selected) + '  ·  Q cycle  ·  Esc close');
      return;
    }
    if (this.ghosting) {
      setPrompt('B — Place lean-to');
      return;
    }
    const hit = this.query(camera, player) ?? (this.nearEmber(player) ? { target: this.emberTarget, distance: 0, lookDot: 1 } : null);
    if (hit) {
      this.refreshPrompts();
      setPrompt(hit.target.prompt);
    }
  }

  get bed(): { x: number; z: number } | null {
    if (!this.placed) {
      return null;
    }
    return { x: this.placed.position.x, z: this.placed.position.z };
  }

  takeSlept(): boolean {
    const did = this.slept;
    this.slept = false;
    return did;
  }

  restore(shelter: boolean, fireLit: boolean): void {
    if (fireLit) {
      this.showFire();
    } else {
      this.hideFire();
    }
    if (shelter && !this.placed) {
      this.seatLeanTo({ x: EMBER.x + 2.4, y: 0, z: EMBER.z, yaw: 0 });
    }
    if (!shelter) {
      this.clearLeanTo();
    }
  }

  publish(hook: CampHook): void {
    hook.shelter = this.shelter;
    hook.fireLit = this.fireLit;
    hook.lastDrink = this.lastDrink;
    if (hook.debug) {
      hook.debug.leanToUp = this.shelter;
      hook.debug.fireLit = this.fireLit;
      hook.debug.buildOpen = this.menuOpen;
      hook.debug.ghosting = this.ghosting;
      hook.debug.pieces = this.yard.pieces.map((piece) => ({
        kind: piece.kind,
        integrity: piece.integrity,
        x: piece.x,
        z: piece.z,
      }));
    }
  }

  cycle(): void {
    const order: PieceKind[] = ['lean-to', 'workbench', 'drying-rack', 'spike-wall'];
    const i = order.indexOf(this.selected);
    const next = order[(i + 1) % order.length];
    this.selected = next ?? 'lean-to';
    if (this.menuOpen) {
      this.showGhostForSelection();
    }
  }

  closeMenu(): boolean {
    if (!this.menuOpen && !this.ghosting) {
      return false;
    }
    this.menuOpen = false;
    this.ghosting = false;
    this.ghostMesh.visible = false;
    return true;
  }

  recipeLine(): string {
    if (!this.menuOpen && !this.ghosting) {
      return '';
    }
    const kinds: PieceKind[] = ['lean-to', 'workbench', 'drying-rack', 'spike-wall'];
    const wood = this.inventory.countOf('wood');
    const fiber = this.inventory.countOf('fiber');
    const lines = [`BUILD  wood ${wood}  fiber ${fiber}`, 'Q cycle  ·  B place  ·  Esc close'];
    for (const kind of kinds) {
      const cost = PIECE_COST[kind];
      const mark = kind === this.selected ? '>' : ' ';
      const ok = this.canAfford(kind) && (kind !== 'lean-to' || !this.shelter);
      lines.push(`${mark} ${kind}  ${cost.wood}w ${cost.fiber}f${ok ? '' : '  need mats'}`);
    }
    return lines.join('\n');
  }

  private onBuild(player: Xyz, yaw: number): boolean {
    if (!this.menuOpen) {
      this.menuOpen = true;
      this.showGhostForSelection();
      applyLeanToPose(this.ghostHold, placeLeanToInFront(player, yaw));
      setPrompt(
        this.ghosting
          ? `B — Place ${this.selected}  ·  Q cycle  ·  Esc close`
          : `${this.needLine(this.selected)}  ·  Q cycle  ·  Esc close`,
      );
      return true;
    }
    if (this.selected !== 'lean-to') {
      const ok = this.placePiece(this.selected, player, yaw);
      if (!ok) {
        setPrompt(this.needLine(this.selected));
      }
      return true;
    }
    if (this.shelter) {
      setPrompt('Lean-to already up');
      return true;
    }
    if (this.ghosting) {
      return this.commitLeanTo(player, yaw);
    }
    if (!canPlaceLeanTo(this.inventory, this.shelter)) {
      setPrompt(this.needLine('lean-to'));
      return true;
    }
    this.ghosting = true;
    this.ghostMesh.visible = true;
    applyLeanToPose(this.ghostHold, placeLeanToInFront(player, yaw));
    return true;
  }

  private canAfford(kind: PieceKind): boolean {
    const cost = PIECE_COST[kind];
    return this.inventory.countOf('wood') >= cost.wood && this.inventory.countOf('fiber') >= cost.fiber;
  }

  private canPreview(): boolean {
    if (this.selected === 'lean-to' && this.shelter) {
      return false;
    }
    return this.canAfford(this.selected);
  }

  private needLine(kind: PieceKind): string {
    const cost = PIECE_COST[kind];
    return `Need ${cost.wood} wood, ${cost.fiber} fiber`;
  }

  private showGhostForSelection(): void {
    this.ghostHold.remove(this.ghostMesh);
    this.ghostMesh = this.selected === 'lean-to' ? createLeanTo(true) : createCampPiece(this.selected, true);
    const show = this.canPreview();
    this.ghostMesh.visible = show;
    this.ghosting = show;
    this.ghostHold.add(this.ghostMesh);
  }

  private use(
    hit: { target: InteractTarget } | null,
    player: Xyz,
    yaw: number,
  ): boolean {
    if (this.ghosting) {
      return this.commitLeanTo(player, yaw);
    }
    const id = hit?.target.id;
    if (id === 'CC-EMBER') {
      return this.useFire();
    }
    if (id === 'CC-WATER' || player.z > OCEAN_DRINK_Z) {
      return this.drinkDirty();
    }
    if (id === 'CC-LEANTO') {
      return false;
    }
    if (id === 'CC-WORKBENCH') {
      return this.useWorkbench();
    }
    if (id === 'CC-DRYING-RACK') {
      return this.useRack();
    }
    if (this.nearEmber(player)) {
      return this.useFire();
    }
    if (player.z > OCEAN_DRINK_Z) {
      return this.drinkDirty();
    }
    return false;
  }

  private useFire(): boolean {
    if (this.fireLit) {
      if (cookRaw(this.inventory, this.vitals, true)) {
        setPrompt('Cooked meat — E again to eat');
        this.refreshPrompts();
        return true;
      }
      if (eatCooked(this.inventory, this.vitals)) {
        setPrompt('Ate cooked meat');
        this.refreshPrompts();
        return true;
      }
      return this.drinkClean();
    }
    return this.lightFire();
  }

  private useWorkbench(): boolean {
    if (cookRaw(this.inventory, this.vitals, true)) {
      setPrompt('Cooked meat on the bench');
      this.refreshPrompts();
      return true;
    }
    if (eatCooked(this.inventory, this.vitals)) {
      setPrompt('Ate cooked meat');
      this.refreshPrompts();
      return true;
    }
    if (canCraftTorch(this.inventory)) {
      const ok = craftTorch({ inventory: this.inventory, give: (id) => this.inventory.add(id, 1) });
      if (ok) {
        setPrompt('Crafted a torch');
      }
      return ok;
    }
    setPrompt('Workbench — bring raw meat to cook, or wood+fiber for a torch');
    return true;
  }

  private useRack(): boolean {
    if (cookRaw(this.inventory, this.vitals, true)) {
      setPrompt('Meat dried');
      this.refreshPrompts();
      return true;
    }
    setPrompt('Drying rack — hang raw meat');
    return true;
  }

  private bindPiece(kind: PieceKind, pose: { x: number; z: number }): void {
    if (kind !== 'workbench' && kind !== 'drying-rack') {
      return;
    }
    const id = kind === 'workbench' ? 'CC-WORKBENCH' : 'CC-DRYING-RACK';
    this.pieceTargets.push({
      id,
      prompt: kind === 'workbench' ? 'E — Workbench' : 'E — Drying rack',
      position: { x: pose.x, y: 0.7, z: pose.z },
      radius: 1.15,
      live: true,
    });
  }

  private lightFire(): boolean {
    if (this.fireLit) {
      return false;
    }
    if (this.inventory.countOf('wood') < WOOD_FIRE) {
      setPrompt('Need 2 wood');
      return true;
    }
    if (!this.inventory.remove('wood', WOOD_FIRE)) {
      return false;
    }
    this.showFire();
    return true;
  }

  private showFire(): void {
    this.fireLit = true;
    this.fireLight.intensity = 2.2;
    this.flames.visible = true;
    const ember = this.scene.getObjectByName('CC-EMBER');
    ember?.traverse((child) => {
      if (child instanceof THREE.Mesh && child.material instanceof THREE.MeshStandardMaterial) {
        child.material.emissive.setHex(0xff6a18);
        child.material.emissiveIntensity = Math.max(child.material.emissiveIntensity, 1.8);
      }
    });
    this.refreshPrompts();
  }

  private hideFire(): void {
    this.fireLit = false;
    this.fireLight.intensity = 0;
    this.flames.visible = false;
    const ember = this.scene.getObjectByName('CC-EMBER');
    ember?.traverse((child) => {
      if (child instanceof THREE.Mesh && child.material instanceof THREE.MeshStandardMaterial) {
        child.material.emissive.setHex(0x000000);
        child.material.emissiveIntensity = 0;
      }
    });
    this.refreshPrompts();
  }

  private seatLeanTo(pose: { x: number; y: number; z: number; yaw: number }): void {
    this.ghostMesh.visible = false;
    this.ghosting = false;
    this.clearLeanTo();
    this.placed = createLeanTo(false);
    applyLeanToPose(this.placed, pose);
    this.scene.add(this.placed);
    const piece = this.yard.place('lean-to', pose, this.placed);
    const at = piece ?? pose;
    this.leanTarget = {
      id: 'CC-LEANTO',
      prompt: 'Hold E — Sleep',
      position: { x: at.x, y: 0.7, z: at.z },
      radius: 1.3,
      live: true,
    };
    this.shelter = true;
  }

  private wreckLeanTo(): void {
    this.placed = null;
    this.leanTarget = null;
    this.shelter = false;
  }

  private clearLeanTo(): void {
    if (this.placed) {
      this.scene.remove(this.placed);
      this.placed = null;
    }
    for (const piece of this.yard.removeKind('lean-to')) {
      if (piece.mesh && piece.mesh !== this.placed) {
        this.scene.remove(piece.mesh);
      }
    }
    this.leanTarget = null;
    this.shelter = false;
  }

  private drinkDirty(): boolean {
    this.vitals.addThirst(DIRTY_THIRST);
    this.lastDrink = performance.now();
    return true;
  }

  private drinkClean(): boolean {
    this.vitals.addThirst(CLEAN_THIRST);
    this.lastDrink = performance.now();
    return true;
  }

  private updateSleep(input: CampTick, hit: { target: InteractTarget } | null): void {
    const onLean = this.shelter && (hit?.target.id === 'CC-LEANTO' || this.nearLean(input.player));
    if (onLean && input.interactDown) {
      this.sleepHold += input.dt;
      if (this.sleepHold >= SLEEP_HOLD_S) {
        this.sleep();
        this.sleepHold = 0;
      }
    } else {
      this.sleepHold = 0;
    }
  }

  private sleep(): void {
    this.day.skipTo(SLEEP_TOD_MIN);
    this.vitals.setFatigue(VITAL_MAX);
    this.vitals.addHunger(SLEEP_HUNGER);
    this.vitals.addThirst(SLEEP_THIRST);
    this.slept = true;
  }

  private targets(): InteractTarget[] {
    const list: InteractTarget[] = [this.emberTarget, this.waterTarget, ...this.pieceTargets];
    if (this.leanTarget) {
      list.push(this.leanTarget);
    }
    return list;
  }

  private refreshPrompts(): void {
    if (!this.fireLit) {
      this.emberTarget.prompt = 'E — Light fire (2 wood)';
      return;
    }
    if (this.inventory.countOf('raw') >= 1) {
      this.emberTarget.prompt = 'E — Cook raw meat';
    } else if (this.inventory.countOf('cooked') >= 1) {
      this.emberTarget.prompt = 'E — Eat cooked meat';
    } else {
      this.emberTarget.prompt = 'E — Drink (boiled)';
    }
  }

  private nearEmber(player: Xyz): boolean {
    return dist(player, EMBER) <= EMBER_USE_RANGE;
  }

  private nearLean(player: Xyz): boolean {
    return this.leanTarget !== null && dist(player, this.leanTarget.position) <= 2.6;
  }
}

function dist(a: Xyz, b: Xyz): number {
  return Math.hypot(a.x - b.x, a.y - b.y, a.z - b.z);
}

function makeFlames(): THREE.Group {
  const group = new THREE.Group();
  group.name = 'CC-EMBER-FLAME';
  const mat = new THREE.MeshStandardMaterial({
    color: 0xff7a22,
    emissive: 0xff4a10,
    emissiveIntensity: 2.2,
    roughness: 0.55,
    metalness: 0.02,
    side: THREE.DoubleSide,
  });
  for (const [x, z, h, w] of [
    [0, 0, 0.55, 0.18],
    [0.08, 0.04, 0.38, 0.12],
    [-0.07, -0.03, 0.42, 0.13],
  ] as const) {
    const card = new THREE.Mesh(new THREE.PlaneGeometry(w, h), mat);
    card.position.set(x, h * 0.45, z);
    group.add(card);
  }
  return group;
}
